import React from 'react';
import { UserType } from '../types';
import { ArrowDown } from 'lucide-react';
import TestimonialCard from './TestimonialCard';

interface HeroSectionProps {
  userType: UserType | null;
  onUserTypeSelect: (type: UserType) => void;
  onStart: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ 
  userType, onUserTypeSelect, onStart 
}) => {
  return (
    <section className="max-w-7xl mx-auto px-4 py-24 md:py-32 grid md:grid-cols-2 gap-10 items-center">
      <div className="hero-content">
        <h1 className="text-4xl md:text-5xl font-bold text-[#103541] leading-tight mb-6">
          Descubra seu potencial de produtividade fiscal
        </h1>
        <p className="text-lg text-[#2E6173] mb-8">
          Faça um diagnóstico rápido e veja quanto tempo e dinheiro sua empresa pode economizar com automação.
        </p>
        
        <div className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-[#103541]">Selecione seu perfil:</h2>
            <ArrowDown className="text-[#7AEA53] animate-bounce" size={24} />
          </div>

          <div className="space-y-4 mb-6">
            <div 
              className={`user-option flex items-center p-5 rounded-xl cursor-pointer border-2 transition-all ${
                userType === 'analyst' ? 'border-[#7AEA53] bg-[#f9fff5]' : 'border-gray-200 bg-white hover:border-[#7AEA53] hover:shadow-md'
              }`}
              onClick={() => onUserTypeSelect('analyst')}
              role="button"
              tabIndex={0}
              aria-label="Selecionar perfil: Analista Fiscal"
            >
              <div className="flex -space-x-3 mr-4">
                <div className="h-10 w-10 rounded-full bg-[#7AEA53] flex items-center justify-center text-white font-semibold text-sm">JC</div>
                <div className="h-10 w-10 rounded-full bg-[#2E6173] flex items-center justify-center text-white font-semibold text-sm">MR</div>
                <div className="h-10 w-10 rounded-full bg-[#5DD0F2] flex items-center justify-center text-white font-semibold text-sm">AS</div>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-[#103541]">Sou analista fiscal/contábil</div>
                <div className="text-sm text-[#2E6173]">Quero automatizar a gestão de documentos fiscais</div>
              </div>
              <div className={`h-6 w-6 rounded-full border-2 ${
                userType === 'analyst' ? 'border-[#7AEA53] bg-[#7AEA53]' : 'border-gray-200'
              }`}>
                {userType === 'analyst' && (
                  <div className="h-2 w-2 bg-white rounded-full m-auto mt-[7px]"></div>
                )}
              </div>
            </div>
            
            <div 
              className={`user-option flex items-center p-5 rounded-xl cursor-pointer border-2 transition-all ${
                userType === 'manager' ? 'border-[#7AEA53] bg-[#f9fff5]' : 'border-gray-200 bg-white hover:border-[#7AEA53] hover:shadow-md'
              }`}
              onClick={() => onUserTypeSelect('manager')}
              role="button"
              tabIndex={0}
              aria-label="Selecionar perfil: Gestor Financeiro"
            >
              <div className="flex -space-x-3 mr-4">
                <div className="h-10 w-10 rounded-full bg-[#7AEA53] flex items-center justify-center text-white font-semibold text-sm">GA</div>
                <div className="h-10 w-10 rounded-full bg-[#2E6173] flex items-center justify-center text-white font-semibold text-sm">LM</div>
                <div className="h-10 w-10 rounded-full bg-[#5DD0F2] flex items-center justify-center text-white font-semibold text-sm">PH</div>
              </div>
              <div className="flex-1">
                <div className="font-semibold text-[#103541]">Sou gestor/empresário</div>
                <div className="text-sm text-[#2E6173]">Busco reduzir custos e eliminar erros fiscais</div>
              </div>
              <div className={`h-6 w-6 rounded-full border-2 ${
                userType === 'manager' ? 'border-[#7AEA53] bg-[#7AEA53]' : 'border-gray-200'
              }`}>
                {userType === 'manager' && (
                  <div className="h-2 w-2 bg-white rounded-full m-auto mt-[7px]"></div>
                )}
              </div>
            </div>
          </div>
          
          <button 
            className={`w-full py-4 px-6 bg-[#7AEA53] hover:bg-[#6DD142] disabled:bg-gray-200 disabled:text-gray-400 text-white font-semibold rounded-xl transition-all transform hover:-translate-y-1 disabled:transform-none disabled:hover:shadow-none flex items-center justify-center gap-2 ${
              !userType ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={!userType}
            onClick={onStart}
          >
            {userType ? 'Iniciar Diagnóstico' : 'Selecione seu perfil para continuar'}
          </button>
          
          <div className="flex justify-center mt-6 space-x-2">
            <div className="h-2 w-6 rounded-sm bg-[#7AEA53]"></div>
            <div className="h-2 w-2 rounded-full bg-gray-200"></div>
            <div className="h-2 w-2 rounded-full bg-gray-200"></div>
          </div>
        </div>
      </div>
      
      <TestimonialCard />
    </section>
  );
};

export default HeroSection;